// START: NEW FILE - Halaman CRUD untuk Lead Sources
import React, { useState, useEffect, useCallback } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  App,
  Popconfirm,
  Space,
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Switch,
} from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import api from '../../../services/api';
import { LeadSource } from '../../../types/leads';
import { useAuth } from '../../../contexts/AuthContext';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const LeadSourceList: React.FC = () => {
  const [leadSources, setLeadSources] = useState<LeadSource[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingSource, setEditingSource] = useState<LeadSource | null>(null);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('leadSource.create');
  const canUpdate = hasPermission('leadSource.update');
  const canDelete = hasPermission('leadSource.delete');

  const fetchLeadSources = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('lead-sources');
      setLeadSources(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch lead sources');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    fetchLeadSources();
  }, [fetchLeadSources]);

  const showModal = (source: LeadSource | null = null) => {
    setEditingSource(source);
    form.setFieldsValue(
      source
        ? { name: source.name, type: source.type, description: source.description }
        : { name: '', type: 'online', description: '' }
    );
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingSource(null);
    form.resetFields();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (editingSource) {
        await api.put(`lead-sources/${editingSource.id}`, values);
        message.success('Lead source updated successfully');
      } else {
        await api.post('lead-sources', values);
        message.success('Lead source created successfully');
      }

      fetchLeadSources();
      handleCancel();
    } catch (error) {
      message.error('An error occurred. Please try again.');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleActive = async (record: LeadSource) => {
    try {
      setLoading(true);
      const action = record.is_active ? 'deactivated' : 'reactivated';
      const endpoint = record.is_active
        ? `/lead-sources/${record.id}`
        : `/lead-sources/${record.id}/reactivate`;

      if (record.is_active) {
        await api.delete(endpoint);
      } else {
        await api.put(endpoint);
      }

      message.success(`Lead source ${action} successfully`);
      fetchLeadSources();
    } catch (error) {
      const action = record.is_active ? 'deactivate' : 'reactivate';
      message.error(`Failed to ${action} lead source`);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a: LeadSource, b: LeadSource) => a.name.localeCompare(b.name),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => (
        <Tag color={type === 'online' ? 'blue' : 'green'}>
          {type.charAt(0).toUpperCase() + type.slice(1)}
        </Tag>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: LeadSource) => (
        <Space size="middle">
          {canUpdate && (
            <Button
              icon={<EditOutlined />}
              onClick={() => showModal(record)}
              disabled={!record.is_active}
            />
          )}
          {canDelete && (
            <Popconfirm
              title={`Are you sure you want to ${
                record.is_active ? 'deactivate' : 'reactivate'
              } this type?`}
              onConfirm={() => handleToggleActive(record)}
              okText="Yes"
              cancelText="No"
            >
              <Switch
                checked={record.is_active}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
              />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Lead Sources Management
          </Title>
        </Col>
        {canCreate && (
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => showModal()}
            >
              Tambah Lead Source
            </Button>
          </Col>
        )}
      </Row>

      <Card bordered={false}>
        <Table
          columns={columns}
          dataSource={leadSources}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingSource ? 'Edit Lead Source' : 'Add New Lead Source'}
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        confirmLoading={loading}
        destroyOnClose
      >
        <Form form={form} layout="vertical" name="leadSourceForm">
          <Form.Item
            name="type"
            label="Source Type"
            rules={[{ required: true, message: 'Please select a type!' }]}
          >
            <Select placeholder="Select a type">
              <Option value="online">Online</Option>
              <Option value="offline">Offline</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={4} />
          </Form.Item>
          <Form.Item
            name="name"
            label="Source Name"
            rules={[{ required: true, message: 'Please input the name!' }]}
          >
            <Input />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LeadSourceList;
// END: NEW FILE - Halaman CRUD untuk Lead Sources
